import React, { createContext, useContext, useEffect, useState } from 'react';
import { ConfigProvider, theme as antdTheme } from 'antd';
import { PRIVATE_THEME } from '../../theme/privateTheme';
import './PrivateUI.css';
import './PrivateUIUtilities.css';
import './PrivateUIDark.css';

const ThemeContext = createContext(null);
const STORAGE_KEY = 'wabot-private-theme';

/** Lit le choix local ; une valeur absente ou invalide suit le système sans bloquer le rendu. */
function readPreference() {
  try { const value = localStorage.getItem(STORAGE_KEY); return ['light', 'dark', 'system'].includes(value) ? value : 'system'; }
  catch { return 'system'; }
}

/** Donne au sélecteur du compte le choix actuel et la fonction qui le mémorise. */
export function usePrivateTheme() { return useContext(ThemeContext); }

/**
 * Applique le thème privé sans toucher aux pages publiques ni aux traitements métier.
 * Le mode système suit les changements du navigateur ; les portails, y compris les
 * confirmations statiques, reçoivent le même attribut de thème que le contenu principal.
 */
const PrivateUIProvider = ({ children }) => {
  const [preference, setPreference] = useState(readPreference);
  const [systemDark, setSystemDark] = useState(() => window.matchMedia?.('(prefers-color-scheme: dark)').matches || false);
  const dark = preference === 'dark' || (preference === 'system' && systemDark);
  const mode = dark ? 'dark' : 'light';

  /** Écoute la préférence du système et retire l'écouteur au démontage du contexte privé. */
  useEffect(() => {
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!media) return undefined;
    /** Actualise uniquement l'apparence lorsque le système change de thème. */
    const update = (event) => setSystemDark(event.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  /** Mémorise le choix et colore les portails présents ou créés après l'ouverture d'une fenêtre. */
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, preference); } catch { /* Le thème reste utilisable si le stockage est indisponible. */ }
    /** Synchronise les surfaces privées seulement ; les éléments publics restent indépendants. */
    const sync = () => document.querySelectorAll('.private-ui').forEach((node) => { node.dataset.privateTheme = mode; });
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [preference, mode]);

  /** Change la préférence validée par les trois options du menu, sans appeler le serveur. */
  const changePreference = (value) => { if (['light', 'dark', 'system'].includes(value)) setPreference(value); };
  const theme = dark ? {
    ...PRIVATE_THEME,
    algorithm: antdTheme.darkAlgorithm,
    token: { ...PRIVATE_THEME.token, colorPrimary: '#cef34a', colorPrimaryHover: '#bddf3e', colorPrimaryActive: '#cef34a', colorPrimaryBg: '#303b1e', colorPrimaryBgHover: '#3c4828', colorPrimaryBorder: '#657936', colorPrimaryText: '#cef34a', colorPrimaryTextHover: '#e0ff78', colorLink: '#cef34a', colorLinkHover: '#e0ff78', colorBgBase: '#151519', colorBgContainer: '#202026', colorBgElevated: '#27272f', colorText: '#f3f3f6', colorTextSecondary: '#b4b4c0', colorBorder: '#44444f', controlOutline: 'rgba(206,243,74,.25)' },
    components: { ...PRIVATE_THEME.components, Table: { rowSelectedBg: '#303b1e', rowSelectedHoverBg: '#3c4828' }, Select: { optionSelectedBg: '#303b1e' } },
  } : PRIVATE_THEME;
  const popupClass = 'private-ui';
  return (
    <ThemeContext.Provider value={{ preference, mode, changePreference }}>
      <ConfigProvider theme={theme} modal={{ className: popupClass }} drawer={{ className: popupClass }} dropdown={{ className: popupClass }} tooltip={{ classNames: { root: popupClass } }} popover={{ classNames: { root: popupClass } }} popconfirm={{ classNames: { root: popupClass } }}>
        <div className="private-ui" data-private-theme={mode}>{children}</div>
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};

export default PrivateUIProvider;
